// src/components/MessageBuyToast.js
//
// The Home page's "recent purchase" social-proof toast — reworked out of
// Footer.js, which previously had two real bugs: any order type other than
// "EBOOK" fell into a hardcoded "PAPERBACK" branch (silently mislabeling
// every AUDIOBOOK purchase), and its close button set a single isVisible
// flag that hid the entire <Footer>, not just this toast.
import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { formatDistanceToNow, differenceInHours } from 'date-fns';
import { useDismissibleWithExpiry } from '../hooks/useDismissibleWithExpiry';
import '../css/messageBuyToast.css';

// Change this one value to tune how long a dismissal lasts before the
// toast is allowed to reappear (e.g. 7 * 24 * 60 * 60 * 1000 for 7 days).
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours
const DISMISS_STORAGE_KEY = 'persist_buy_toast_dismissed_until';

const PRODUCT_CONFIG = {
  EBOOK: {
    image: '/images/new_images/11.png',
    alt: 'eBook',
    message: (qty) => `bought ${qty} eBook ${qty > 1 ? 'copies' : 'copy'}!`,
    cta: 'Get the eBook',
  },
  AUDIOBOOK: {
    image: '/images/new_images/10.png',
    alt: 'Audiobook',
    message: (qty) => `bought ${qty} audiobook ${qty > 1 ? 'copies' : 'copy'}!`,
    cta: 'Get the Audiobook',
  },
  PAPERBACK: {
    image: '/images/new_images/3.png',
    alt: 'Paperback',
    message: (qty) => `bought ${qty} ${qty > 1 ? 'copies' : 'copy'}!`,
    cta: 'Get the Book',
  },
  HARDCOVER: {
    image: '/images/new_images/3.png',
    alt: 'Hardcover',
    message: (qty) => `bought ${qty} ${qty > 1 ? 'copies' : 'copy'}!`,
    cta: 'Get the Book',
  },
};

// Cap how many recent orders the toast cycles through — the API
// (PreOrderController::listpreorder) already returns newest-first, so this
// just keeps the slider from having to cycle through the entire order
// history.
const MAX_RECENT_ORDERS = 20;

function MessageBuyToast() {
  const location = useLocation();
  const isHome = location.pathname === '/';

  const [orders, setOrders] = useState([]);
  const [dismissed, dismiss] = useDismissibleWithExpiry(DISMISS_STORAGE_KEY, DISMISS_DURATION_MS);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (!isHome || dismissed) return;

    let cancelled = false;

    async function fetchRecentOrders() {
      try {
        const result = await fetch('https://juniorpersist.com/public/public/api/listpreorder');
        const data = await result.json();
        if (cancelled) return;

        const transformed = data
          // Unrecognized/legacy types are skipped rather than silently
          // mislabeled as PAPERBACK (the original bug).
          .filter((order) => PRODUCT_CONFIG[order.type])
          .slice(0, MAX_RECENT_ORDERS)
          .map((order) => {
            const createdAtDate = new Date(order.created_at);
            const isOld = differenceInHours(new Date(), createdAtDate) >= 48;
            return {
              ...order,
              created_at: isOld ? 'Recently' : formatDistanceToNow(createdAtDate, { addSuffix: true }),
            };
          });

        setOrders(transformed);
      } catch (err) {
        // A social-proof toast is decorative, not critical — fail
        // silently rather than surface an error banner for it.
      }
    }

    fetchRecentOrders();

    return () => { cancelled = true; };
  }, [isHome, dismissed]);

  function handleClose() {
    setClosing(true);
    // Let the fade-out transition finish playing before actually
    // persisting the dismissal and unmounting.
    setTimeout(() => dismiss(), 300);
  }

  if (!isHome || dismissed || orders.length === 0) return null;

  // infinite looping needs at least 2 slides to loop between — with
  // exactly 1 recent order, react-slick would otherwise warn/misbehave.
  const sliderSettings = {
    infinite: orders.length > 1,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: orders.length > 1,
    speed: 250,
    autoplaySpeed: 4000,
    cssEase: 'linear',
    arrows: false,
    pauseOnHover: true,
  };

  return (
    <div className={`buy-toast-wrap ${closing ? 'buy-toast-closing' : 'buy-toast-open'}`}>
      <button onClick={handleClose} className="buy-toast-close" aria-label="Dismiss">
        <i className="bi bi-x-lg"></i>
      </button>
      <Slider {...sliderSettings}>
        {orders.map((order, index) => {
          const config = PRODUCT_CONFIG[order.type];
          return (
            <div key={order.id ?? index}>
              <div className="buy-toast-card">
                <img src={config.image} className="buy-toast-image" alt={config.alt} />
                <div className="buy-toast-body">
                  <div className="buy-toast-line">
                    {(order.name || 'Someone').substring(0, 12)} in{' '}
                    <span className="text-uppercase">
                      {order.country == null ? 'USA' : order.country.substring(0, 10)}
                    </span>
                  </div>
                  <div className="buy-toast-line buy-toast-line-strong">
                    {config.message(order.qty)}
                  </div>
                  <div className="buy-toast-meta">
                    <i className="bi bi-clock-history"></i>
                    <span>{order.created_at}</span>
                  </div>
                  <a href="/BuyBook" className="buy-toast-cta">{config.cta} &rarr;</a>
                </div>
              </div>
            </div>
          );
        })}
      </Slider>
    </div>
  );
}

export default MessageBuyToast;
